/*
 * Pantalla "Mi zona" del modulo IGI (/igi/mi-zona). Pensada para celular: tarjetas apiladas.
 * Lee GET /igi/resumen/ (redux igi_module._get_resumen: data.datos = zonas, data.alertas).
 * El jefe zonal ve solo su zona; director y asesor (mas de una zona) tienen selector de territorio,
 * que filtra en el navegador lo que el servidor ya autorizo (o un ?zona= en la URL, ver mas abajo).
 * Cada tienda muestra E1, E2, E3, IGI, nivel, fecha de la evaluacion vigente, cantidad de visitas y
 * la accion de la evaluacion vigente (backend, igi/selectors/summary.py); al tocarla abre
 * /igi/tiendas/:id. El promedio de eje por zona (Capacidad/Externas/Tareas, 3 tarjetas arriba de la
 * tabla) se calcula aqui mismo, en el navegador, a partir de las tiendas ya traidas (mismo patron
 * simple-promedio que igi_promedio de zona, sin pedir nada nuevo al backend).
 * ?zona=<id> en la URL preselecciona esa zona (usado por el boton "Ver zona" de Pulso).
 */
import React, {useEffect, useMemo, useState} from "react";
import Breadcrumbs from "../../../../components/breadcrumbs";
import {useDispatch, useSelector} from "react-redux";
import {useHistory, useLocation} from "react-router-dom";
import {get_igi_resumen} from "../../../../redux/igi";
import {NivelBadge, formatoFecha, formatoNota} from "../niveles";

/** @module  Pages/Igi/MiZona */

const TODAS = "todas";

const promedioEje = (tiendas, eje) => {
    const valores = tiendas.map((t) => t[eje]).filter((v) => v !== null && v !== undefined);
    if (valores.length === 0) {
        return null;
    }
    return valores.reduce((a, b) => a + b, 0) / valores.length;
};

const MiZona = () => {
    const dispatch = useDispatch();
    const history = useHistory();
    const location = useLocation();
    const {token} = useSelector((state) => state.users_module.login.data) || null;
    const resumen = useSelector((state) => state.igi_module._get_resumen);
    const zonaInicial = new URLSearchParams(location.search).get("zona") || TODAS;
    const [zonaId, setZonaId] = useState(zonaInicial);
    // resumen.status queda en 0 tanto antes de pedir como mientras la peticion esta en curso
    // (redux/igi.js solo lo mueve a 200 o al status de error al terminar); "pedido" distingue
    // ese "en curso" del estado inicial, igual que hace pages/admin/attendance con status "idle".
    const [pedido, setPedido] = useState(false);

    useEffect(() => {
        setPedido(true);
        dispatch(get_igi_resumen(token));
    }, [token, dispatch]);

    const cargando = pedido && resumen.status === 0;

    const zonas = useMemo(() => (resumen.data && resumen.data.datos ? resumen.data.datos : []), [resumen]);
    const alertas = useMemo(() => (resumen.data && resumen.data.alertas ? resumen.data.alertas : []), [resumen]);

    const zonasVisibles = zonaId === TODAS ? zonas : zonas.filter((z) => String(z.id) === String(zonaId));
    const idsVisibles = zonasVisibles.map((z) => z.id);
    const alertasVisibles = alertas.filter((a) => idsVisibles.includes(a.zona_id));

    const abrir = (tiendaId) => history.push(`/igi/tiendas/${tiendaId}`);

    return (
        <div className="app-content content">
            <div className="content-overlay"/>
            <div className="content-wrapper">
                <div className="content-header row">
                    <div className="content-header-left col-12 mb-2 mt-1">
                        <Breadcrumbs title="Mi zona" items={[{label: "Mi zona", link: "/igi/mi-zona"}]}/>
                    </div>
                </div>
                <div className="content-body">
                    {cargando && (
                        <div className="alert alert-info" role="status" aria-busy="true">
                            <i className="bx bx-loader-alt bx-spin mr-50"/> Cargando su zona…
                        </div>
                    )}
                    {resumen.status !== 0 && resumen.status !== 200 && (
                        <div className="alert alert-danger">{resumen.message || "No se pudo cargar el resumen"}</div>
                    )}
                    {zonas.length > 1 && (
                        <div className="form-group">
                            <label htmlFor="igi-zona">Territorio</label>
                            <select
                                id="igi-zona"
                                className="form-control"
                                value={zonaId}
                                onChange={(e) => setZonaId(e.target.value)}
                            >
                                <option value={TODAS}>Todas las zonas</option>
                                {zonas.map((z) => (
                                    <option key={z.id} value={z.id}>{z.nombre}</option>
                                ))}
                            </select>
                        </div>
                    )}
                    {resumen.status === 200 && zonas.length === 0 && (
                        <div className="alert alert-info">No tiene zonas asignadas en IGI.</div>
                    )}
                    {alertasVisibles.length > 0 && (
                        <div className="card border border-danger mb-1">
                            <div className="card-body p-1">
                                <h6 className="text-danger mb-50">
                                    <i className="bx bx-error"/> Alertas (IGI menor a 4,0): {alertasVisibles.length}
                                </h6>
                                {alertasVisibles.map((a) => (
                                    <button
                                        key={a.tienda}
                                        type="button"
                                        className="btn btn-link p-0 d-block text-left"
                                        onClick={() => abrir(a.tienda)}
                                    >
                                        {a.codigo} · {a.nombre} — {formatoNota(a.igi)} ({a.nivel})
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}
                    {zonasVisibles.map((zona) => (
                        <div key={zona.id} className="mb-2">
                            <div className="card mb-1">
                                <div className="card-body p-1">
                                    <h5 className="mb-0">{zona.nombre}</h5>
                                    <small className="text-muted">{zona.jefe.nombres || zona.jefe.usuario}</small>
                                    <div className="d-flex flex-wrap mt-50">
                                        <span className="badge badge-success mr-50 mb-50">Fuertes: {zona.conteo.fuerte}</span>
                                        <span className="badge badge-warning mr-50 mb-50">Medias: {zona.conteo.media}</span>
                                        <span className="badge badge-danger mr-50 mb-50">Débiles: {zona.conteo.debil}</span>
                                        <span className="badge badge-secondary mr-50 mb-50">Sin IGI: {zona.conteo.sin_igi}</span>
                                        <span className="badge badge-primary mr-50 mb-50">Completitud: {zona.completitud}%</span>
                                        {zona.acciones_pendientes > 0 && (
                                            <span className="badge badge-warning mr-50 mb-50">
                                                ⏳ {zona.acciones_pendientes} acción(es) pendiente(s)
                                            </span>
                                        )}
                                        <span className="badge badge-light mr-50 mb-50">
                                            👣 Visitas: {zona.visitas}
                                        </span>
                                    </div>
                                    <div className="d-flex flex-wrap mt-50" style={{gap: "0.5rem"}}>
                                        <div className="text-center px-1">
                                            <div className="small text-muted">Capacidad E1</div>
                                            <strong>{formatoNota(promedioEje(zona.tiendas, "e1"))}</strong>
                                        </div>
                                        <div className="text-center px-1">
                                            <div className="small text-muted">Externas E2</div>
                                            <strong>{formatoNota(promedioEje(zona.tiendas, "e2"))}</strong>
                                        </div>
                                        <div className="text-center px-1">
                                            <div className="small text-muted">Tareas E3</div>
                                            <strong>{formatoNota(promedioEje(zona.tiendas, "e3"))}</strong>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="row">
                                {zona.tiendas.map((t) => (
                                    <div key={t.id} className="col-12 col-md-6 col-xl-4">
                                        <div
                                            className="card mb-1"
                                            role="button"
                                            tabIndex={0}
                                            style={{cursor: "pointer"}}
                                            onClick={() => abrir(t.id)}
                                            onKeyDown={(e) => {
                                                if (e.key === "Enter") {
                                                    abrir(t.id);
                                                }
                                            }}
                                        >
                                            <div className="card-body p-1">
                                                <div className="d-flex justify-content-between align-items-start">
                                                    <div>
                                                        <strong>{t.codigo}</strong>{" "}
                                                        {t.alerta && <i className="bx bx-error text-danger" title="Alerta"/>}
                                                        <div className="text-muted small">{t.nombre}</div>
                                                    </div>
                                                    <div className="text-right">
                                                        <div style={{fontSize: "1.4rem", fontWeight: 700}}>{formatoNota(t.igi)}</div>
                                                        <NivelBadge nivel={t.nivel}/>
                                                    </div>
                                                </div>
                                                <div className="d-flex justify-content-between small mt-50">
                                                    <span>E1 {formatoNota(t.e1)}</span>
                                                    <span>E2 {formatoNota(t.e2)}</span>
                                                    <span>E3 {formatoNota(t.e3)}</span>
                                                </div>
                                                <div className="small text-muted mt-25">
                                                    Última evaluación: {formatoFecha(t.fecha_ultima_evaluacion)}
                                                </div>
                                                <div className="small text-muted mt-25">
                                                    Última visita: {formatoFecha(t.ultima_visita)} · Visitas: {t.visitas}
                                                </div>
                                                {t.accion && (
                                                    <div className="small mt-25" style={{whiteSpace: "pre-wrap"}}>
                                                        <strong>Acción:</strong> {t.accion}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};

export default MiZona;
